import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, Utensils, Sun, Cookie, GlassWater, ChevronDown, Search, X, ChefHat,
  ShoppingBag, Plus, Minus, Trash2, ArrowRight, Sparkles, Star, Check, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MenuItemCard from '@/components/MenuItemCard';
import { useCart } from '@/context/CartContext';
import api from '@/utils/apiClient';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import WebUpsellPopup from '@/components/ordering/WebUpsellPopup';
import WebCouponInput from '@/components/ordering/WebCouponInput';
import WebAccessibilityToggle from '@/components/ordering/WebAccessibilityToggle';
import { MenuSchema, BreadcrumbSchema } from '@/components/StructuredData';
import { CoffeePassMenuCallout } from '@/components/CoffeePassPromo';
import LocationDisclaimer from '@/components/LocationDisclaimer';
import { useMenuSync } from '@/hooks/useMenuSync';

// Category hero images - appropriate images for each category
const CATEGORY_IMAGES = {
  "all": "https://images.unsplash.com/photo-1564759224907-65b945ff0e84?auto=format&fit=crop&w=1200&q=80",
  "breakfast": "https://images.unsplash.com/photo-1653169836575-f929203b99a1?auto=format&fit=crop&w=1200&q=80",
  "lunch": "https://images.unsplash.com/photo-1762922425249-144c3bb9167e?auto=format&fit=crop&w=1200&q=80",
  "coffee": "https://images.pexels.com/photos/8736400/pexels-photo-8736400.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "sweets": "https://images.unsplash.com/photo-1767044315790-bc0fe664fd0b?auto=format&fit=crop&w=1200&q=80",
  "desserts": "https://images.unsplash.com/photo-1767044315790-bc0fe664fd0b?auto=format&fit=crop&w=1200&q=80",
  "iced-drinks": "https://images.unsplash.com/photo-1551994179-cbac7ecb9428?auto=format&fit=crop&w=1200&q=80",
  "smoothies-blends": "https://images.unsplash.com/photo-1676159434936-9c19c551d262?auto=format&fit=crop&w=1200&q=80",
  "fresh-mixed-juice": "https://images.unsplash.com/photo-1617218607489-4d28d612bd07?auto=format&fit=crop&w=1200&q=80",
  "milk-shakes": "https://images.unsplash.com/photo-1761637592257-3c5911ae5a3a?auto=format&fit=crop&w=1200&q=80",
  "drinks": "https://images.unsplash.com/photo-1551994179-cbac7ecb9428?auto=format&fit=crop&w=1200&q=80",
  "breads": "https://images.unsplash.com/photo-1608198093002-ad4e005484ec?auto=format&fit=crop&w=1200&q=80",
  "bread": "https://images.unsplash.com/photo-1608198093002-ad4e005484ec?auto=format&fit=crop&w=1200&q=80",
  "muffins": "https://images.unsplash.com/photo-1607958996333-41aef7caefaa?auto=format&fit=crop&w=1200&q=80",
  "pastries": "https://images.unsplash.com/photo-1509365465985-25d11c17e812?auto=format&fit=crop&w=1200&q=80",
  "pies": "https://images.unsplash.com/photo-1535920527002-b35e96722eb9?auto=format&fit=crop&w=1200&q=80"
};

const categories = [
  { id: 'all', label: 'All', icon: null },
  { id: 'build-your-own', label: 'Build Your Own', icon: ChefHat, isSpecial: true },
  { id: 'coffee', label: 'Coffee', icon: Coffee },
  { id: 'breakfast', label: 'Breakfast', icon: Sun },
  { id: 'lunch', label: 'Lunch', icon: Utensils },
  { id: 'salads', label: 'Salads', icon: Utensils },
  { id: 'muffins', label: 'Muffins', icon: Cookie },
  { id: 'pastries', label: 'Pastries', icon: Cookie },
  { id: 'pies', label: 'Pies', icon: Cookie },
  { id: 'breads', label: 'Breads', icon: Cookie },
  { id: 'sweets', label: 'Sweets', icon: Cookie },
  { id: 'desserts', label: 'Desserts', icon: Cookie },
  { id: 'drinks', label: 'Drinks', icon: GlassWater, hasSubmenu: true, submenu: [
    { id: 'iced-drinks', label: 'Iced Drinks' },
    { id: 'smoothies-blends', label: 'Smoothies & Blends' },
    { id: 'fresh-mixed-juice', label: 'Fresh Mixed Juice' },
    { id: 'milk-shakes', label: 'Milk Shakes' },
  ]},
];

// Category ID to display config mapping
const CATEGORY_CONFIG = {
  'all': { label: 'All', icon: null },
  'build-your-own': { label: 'Build Your Own', icon: ChefHat, isSpecial: true },
  'coffee': { label: 'Coffee', icon: Coffee },
  'breakfast': { label: 'Breakfast', icon: Sun },
  'lunch': { label: 'Lunch', icon: Utensils },
  'salads': { label: 'Salads', icon: Utensils },
  'muffins': { label: 'Muffins', icon: Cookie },
  'pastries': { label: 'Pastries', icon: Cookie },
  'pies': { label: 'Pies', icon: Cookie },
  'breads': { label: 'Breads', icon: Cookie },
  'sweets': { label: 'Sweets', icon: Cookie },
  'desserts': { label: 'Desserts', icon: Cookie },
  'iced-drinks': { label: 'Iced Drinks', icon: GlassWater },
  'smoothies-blends': { label: 'Smoothies Blends', icon: GlassWater },
  'fresh-mixed-juice': { label: 'Fresh Mixed Juice', icon: GlassWater },
  'milk-shakes': { label: 'Milk Shakes', icon: GlassWater },
  'drinks': { label: 'Drinks', icon: GlassWater },
};

// Order Progress Steps
const ORDER_STEPS = [
  { id: 'menu', label: 'Browse Menu', icon: Coffee },
  { id: 'cart', label: 'Review Cart', icon: ShoppingBag },
  { id: 'details', label: 'Your Details', icon: Check },
  { id: 'confirm', label: 'Confirm', icon: Sparkles }
];

export default function MenuPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cart, addToCart, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();
  const [menuItems, setMenuItems] = useState([]);
  
  // Initialize category from URL param, fallback to 'all'
  const urlCategory = searchParams.get('category');
  const [activeCategory, setActiveCategoryState] = useState(urlCategory || 'all');
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [showDrinksSubmenu, setShowDrinksSubmenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [upsells, setUpsells] = useState([]);
  const [quickAddIds, setQuickAddIds] = useState([]);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [displayCategories, setDisplayCategories] = useState(categories); // Dynamic categories from layout
  
  // Custom setActiveCategory that also updates URL
  const setActiveCategory = (categoryId) => {
    setActiveCategoryState(categoryId);
    // Update URL with category param
    if (categoryId === 'all') {
      // Remove category param when 'all' is selected
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('category');
      setSearchParams(newParams, { replace: true });
    } else {
      setSearchParams({ category: categoryId }, { replace: true });
    }
  };

  // Sync with URL if it changes externally (e.g., browser back/forward)
  useEffect(() => {
    const urlCat = searchParams.get('category');
    if (urlCat && urlCat !== activeCategory) {
      setActiveCategoryState(urlCat);
    } else if (!urlCat && activeCategory !== 'all') {
      setActiveCategoryState('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Scroll position persistence - restore on page load
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('menu-scroll-position');
    if (savedScroll) {
      const scrollData = JSON.parse(savedScroll);
      setTimeout(() => {
        window.scrollTo(0, scrollData.y);
      }, 100);
      sessionStorage.removeItem('menu-scroll-position');
    }
  }, []);

  // Save scroll position before page unload (refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('menu-scroll-position', JSON.stringify({
        y: window.scrollY
      }));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // New UX Enhancement States
  const [showUpsellPopup, setShowUpsellPopup] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [loyaltyMember, setLoyaltyMember] = useState(null);
  const [accessibilityEnabled, setAccessibilityEnabled] = useState(() => {
    // Load from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('accessibility-mode') === 'true';
    }
    return false;
  });
  
  // Save accessibility preference
  useEffect(() => {
    localStorage.setItem('accessibility-mode', accessibilityEnabled);
  }, [accessibilityEnabled]);
  
  // Ref for drinks button
  const drinksButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });
  
  // Update dropdown position when it opens
  useEffect(() => {
    if (showDrinksSubmenu && drinksButtonRef.current) {
      const rect = drinksButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        left: Math.max(16, Math.min(rect.left, window.innerWidth - 200)),
        top: rect.bottom + 4
      });
      
      // Close dropdown when scrolling (standard UX behavior)
      const closeOnScroll = () => setShowDrinksSubmenu(false);
      
      // Listen for horizontal scroll on the category tabs
      const scrollContainer = document.querySelector('.scrollbar-hide');
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', closeOnScroll);
      }
      
      return () => {
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', closeOnScroll);
        }
      };
    }
  }, [showDrinksSubmenu]);

  useEffect(() => {
    // Fetch menu items, add-ons, quick-add items, and layout order
    // Add timestamp to bust cache
    const cacheBuster = `?_t=${Date.now()}`;
    Promise.all([
      api.get(`/menu${cacheBuster}`).then(res => res.data),
      api.get(`/add-ons${cacheBuster}`).then(res => res.data),
      api.get(`/menu/quick-add${cacheBuster}`).then(res => res.data).catch(() => ({ quick_add_items: [] })),
      api.get(`/layout/categories${cacheBuster}`).then(res => res.data).catch(() => ({ category_order: [] }))
    ]).then(([menuData, addOnsData, quickAddData, layoutData]) => {
      // Ensure menuData is an array
      const items = Array.isArray(menuData) ? menuData : (menuData?.items || menuData?.menu || []);
      setMenuItems(items);
      setAvailableAddOns(addOnsData);
      setQuickAddIds(quickAddData.quick_add_items || []);
      
      // Build dynamic categories from layout order
      if (layoutData.category_order && layoutData.category_order.length > 0) {
        const orderedCats = [{ id: 'all', label: 'All', icon: null }]; // All always first
        
        // Add "Build Your Own" as second (if not in saved order)
        orderedCats.push({ id: 'build-your-own', label: 'Build Your Own', icon: ChefHat, isSpecial: true });
        
        // Sort categories by display_order from layout
        const sortedLayoutCats = [...layoutData.category_order].sort((a, b) => a.display_order - b.display_order);
        
        // Map saved categories to display config
        sortedLayoutCats.forEach(cat => {
          const catId = cat.category;
          // Skip "all" and "build-your-own" as they're already added
          if (catId === 'all' || catId === 'build-your-own') return;
          
          // Use name from API first, then fallback to hardcoded config, then generate from ID
          const config = CATEGORY_CONFIG[catId] || {};
          const displayName = cat.name || config.label || catId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          
          orderedCats.push({
            id: catId,
            label: displayName,
            icon: config.icon || Coffee,
            isSpecial: config.isSpecial
          });
        });
        
        setDisplayCategories(orderedCats);
      }
      
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  // Function to refetch menu (for real-time sync)
  const fetchMenu = useCallback(() => {
    const cacheBuster = `?_t=${Date.now()}`;
    Promise.all([
      api.get(`/menu${cacheBuster}`).then(res => res.data),
      api.get(`/add-ons${cacheBuster}`).then(res => res.data),
    ]).then(([menuData, addOnsData]) => {
      const items = Array.isArray(menuData) ? menuData : (menuData?.items || menuData?.menu || []);
      setMenuItems(items);
      setAvailableAddOns(addOnsData);
    }).catch(err => {
      console.error('Failed to refresh menu:', err);
    });
  }, []);

  // ============== REAL-TIME MENU SYNC ==============
  // Listen for menu changes from admin panel and auto-refresh
  const handleMenuUpdate = useCallback((data) => {
    console.log('[OnlineMenu] Menu update received:', data);
    // Silently refresh menu - no toast to avoid disrupting customer
    fetchMenu();
  }, [fetchMenu]);

  // Connect to menu sync WebSocket
  useMenuSync(handleMenuUpdate);

  // Fetch upsell suggestions when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      fetchUpsells();
    } else {
      setUpsells([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const fetchUpsells = async () => {
    try {
      const res = await api.post('/upsells/suggest', {
        cart_items: cart.map(item => ({
          item_id: item.id || item.item_id,
          name: item.name,
          category: item.category,
          quantity: item.quantity
        })),
        limit: 3
      });
      setUpsells(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch upsells:', err);
    }
  };

  // Get all drinks subcategory IDs
  const drinksSubcategoryIds = displayCategories.find(c => c.id === 'drinks')?.submenu?.map(s => s.id) || [];

  // Filter items based on category and search
  const filteredItems = (() => {
    let items = menuItems;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
    
    if (activeCategory === 'all') return items;
    if (activeSubcategory) {
      return items.filter(item => item.category === activeSubcategory);
    }
    if (activeCategory === 'drinks') {
      return items.filter(item => drinksSubcategoryIds.includes(item.category));
    }
    return items.filter(item => item.category === activeCategory);
  })();

  const handleCategoryClick = (cat, e) => {
    if (cat.isSpecial && cat.id === 'build-your-own') {
      window.location.href = '/build-your-own';
      return;
    }
    if (cat.hasSubmenu) {
      e.stopPropagation(); // Prevent click outside handler from firing
      setShowDrinksSubmenu(prev => !prev);
      if (activeCategory !== 'drinks') {
        setActiveCategory('drinks');
        setActiveSubcategory(null);
      }
    } else {
      setActiveCategory(cat.id);
      setActiveSubcategory(null);
      setShowDrinksSubmenu(false);
    }
  };

  const handleSubcategoryClick = (subId, e) => {
    e.stopPropagation(); // Prevent click outside handler from firing
    setActiveSubcategory(subId);
    setActiveCategory('drinks');
    setShowDrinksSubmenu(false);
  };

  // Quick add handler for simple items - uses BASE price, not first size
  const handleQuickAdd = (item) => {
    const price = item.price || item.sizes?.[0]?.price;
    addToCart({
      id: item.id,
      name: item.name,
      price: price,
      quantity: 1,
      image: item.image,
      category: item.category,
      selectedSize: null  // Don't auto-select any size - base price is the default
    });
    
    // Trigger upsell popup
    setLastAddedItem(item);
    setShowUpsellPopup(true);
  };

  // Handle adding item from upsell popup
  const handleAddUpsellItem = (item) => {
    addToCart(item);
  };

  // Check if item can be quick-added
  const canQuickAdd = (item) => {
    return quickAddIds.includes(item.id) || 
           !item.sizes || 
           item.sizes.length <= 1;
  };

  // Get current category image
  const currentCategoryImage = CATEGORY_IMAGES[activeSubcategory || activeCategory] || CATEGORY_IMAGES.all;

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDrinksSubmenu && !e.target.closest('.drinks-submenu-container')) {
        setShowDrinksSubmenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDrinksSubmenu]);

  return (
    <div className="pt-20 pb-16 min-h-screen overflow-x-hidden" data-testid="menu-page">
      <SEO {...SEO_CONFIG.menu} path="/menu" />
      
      {/* Structured Data for Menu */}
      <MenuSchema menuItems={(Array.isArray(menuItems) ? menuItems : []).slice(0, 30).map(item => ({
        name: item.name,
        description: item.description || '',
        price: item.price?.toString() || item.sizes?.[0]?.price?.toString() || '0'
      }))} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://bulaccinocafe.com' },
        { name: 'Menu', url: 'https://bulaccinocafe.com/menu' }
      ]} />
      
      {/* Order Progress Indicator */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {ORDER_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === 'menu';
              const isComplete = false;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : isComplete 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{step.label}</span>
                  </div>
                  {index < ORDER_STEPS.length - 1 && (
                    <div className={`w-4 sm:w-8 h-0.5 mx-1 ${
                      isComplete ? 'bg-green-500' : 'bg-muted'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex overflow-x-hidden">
        {/* Main Content Area */}
        <div className="flex-1 lg:pr-80 xl:pr-96 min-w-0">
          {/* Category Hero Banner */}
          <section className="relative h-48 sm:h-64 overflow-hidden">
            <div className="absolute inset-0">
              <img 
                src={currentCategoryImage}
                alt={activeCategory}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            </div>
            <div className="absolute inset-0 flex items-end">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pb-6 w-full">
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-foreground">
                  {activeSubcategory 
                    ? displayCategories.find(c => c.id === 'drinks')?.submenu?.find(s => s.id === activeSubcategory)?.label
                    : activeCategory === 'all' 
                      ? 'Our Menu' 
                      : displayCategories.find(c => c.id === activeCategory)?.label || activeCategory}
                </h1>
                <p className="text-muted-foreground mt-2">
                  Fresh, vibrant, and crafted with love
                </p>
              </div>
            </div>
          </section>

          {/* Search Bar */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 -mt-4 relative z-10">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search menu..."
                className="pl-12 pr-12 h-12 rounded-full bg-card border-border shadow-lg"
                data-testid="menu-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Category Tabs */}
          <section className="sticky top-20 z-40 bg-background/95 backdrop-blur-lg border-b border-border mt-4 pb-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
              <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
                {displayCategories.map(cat => (
                  <div key={cat.id} className="relative drinks-submenu-container flex-shrink-0">
                    <button
                      ref={cat.id === 'drinks' ? drinksButtonRef : null}
                      onClick={(e) => handleCategoryClick(cat, e)}
                      data-testid={`category-${cat.id}`}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium whitespace-nowrap transition-all ${
                        cat.isSpecial
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                          : (activeCategory === cat.id || (cat.id === 'drinks' && drinksSubcategoryIds.includes(activeSubcategory || '')))
                            ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                            : 'bg-card text-foreground border border-border hover:border-primary/50'
                      }`}
                    >
                      {cat.icon && <cat.icon className="w-4 h-4" />}
                      {cat.label}
                      {cat.hasSubmenu && (
                        <ChevronDown className={`w-4 h-4 transition-transform ${showDrinksSubmenu ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* Drinks Submenu - positioned outside backdrop-filter section */}
          {showDrinksSubmenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed bg-card rounded-xl shadow-xl border border-border p-2 z-[100] whitespace-nowrap"
              style={{
                left: dropdownPosition.left,
                top: dropdownPosition.top
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {displayCategories.find(c => c.id === 'drinks')?.submenu?.map(sub => (
                <button
                  key={sub.id}
                  onClick={(e) => handleSubcategoryClick(sub.id, e)}
                  data-testid={`subcategory-${sub.id}`}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap ${
                    activeSubcategory === sub.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </motion.div>
          )}

          {/* Menu Grid */}
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
              {/* Coffee Pass Callout - Show when Coffee category is selected */}
              {(activeCategory === 'coffee' || activeCategory === 'all') && (
                <div className="mb-6">
                  <CoffeePassMenuCallout />
                </div>
              )}
              
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="bg-card rounded-2xl h-80 animate-pulse" />
                  ))}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <Coffee className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No items found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="group max-w-md mx-auto sm:max-w-none w-full"
                    >
                      <div className="relative">
                        <MenuItemCard 
                          item={item} 
                          availableAddOns={availableAddOns} 
                          onAddToCart={() => {
                            // Trigger upsell popup after adding item to cart
                            setLastAddedItem(item);
                            setShowUpsellPopup(true);
                            // Scroll to top of category section after adding to cart
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        />
                        
                        {/* Badges */}
                        <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
                          {item.is_bestseller && (
                            <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3" /> Bestseller
                            </span>
                          )}
                          {item.is_featured && (
                            <span className="px-2.5 py-1 bg-purple-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Location Disclaimer */}
            <LocationDisclaimer className="mt-8 mb-4 max-w-2xl mx-auto px-4" />
          </section>
        </div>

        {/* Persistent Cart Sidebar - Desktop */}
        <aside className="hidden lg:block fixed right-0 top-20 bottom-0 w-80 xl:w-96 bg-card border-l border-border overflow-hidden z-30">
          <div className="h-full flex flex-col">
            {/* Cart Header */}
            <div className="p-4 border-b border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  <span className="font-heading text-lg text-foreground">Your Order</span>
                </div>
                <span className="px-2.5 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                  {cartCount} {cartCount === 1 ? 'item' : 'items'}
                </span>
              </div>
              
              {/* Progress Incentive Bar */}
              </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium">Your cart is empty</p>
                  <p className="text-sm text-muted-foreground mt-1">Add items from the menu</p>
                </div>
              ) : (
                <>
                  {cart.map((item, index) => (
                    <motion.div
                      key={item.cart_key || index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-background rounded-xl p-3 border border-border"
                    >
                      <div className="flex gap-3">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground text-sm truncate">{item.name}</h4>
                          {item.selectedSize && (
                            <p className="text-xs text-muted-foreground">{item.selectedSize}</p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-muted/80"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-muted/80"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <span className="font-bold text-foreground">
                              ${((item.price || 0) * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.cart_key)}
                          className="text-muted-foreground hover:text-destructive self-start"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </>
              )}
            </div>

            {/* Coupon/Loyalty Section */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border">
                <WebCouponInput
                  couponCode={couponCode}
                  onCouponChange={setCouponCode}
                  onValidCoupon={setAppliedDiscount}
                  onLoyaltyMemberFound={setLoyaltyMember}
                />
              </div>
            )}

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border bg-muted/30">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium text-foreground">${cartTotal.toFixed(2)}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex items-center justify-between text-sm text-green-600 dark:text-green-400">
                      <span className="flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        {appliedDiscount.from_loyalty ? 'Loyalty Discount' : 'Promo Code'} ({appliedDiscount.discount_percent}%)
                      </span>
                      <span>-${(cartTotal * appliedDiscount.discount_percent / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-muted-foreground font-medium">Total</span>
                    <span className="text-2xl font-bold text-foreground">
                      ${appliedDiscount 
                        ? (cartTotal * (1 - appliedDiscount.discount_percent / 100)).toFixed(2)
                        : cartTotal.toFixed(2)
                      }
                    </span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/order', { 
                    state: { 
                      discount: appliedDiscount,
                      loyaltyMember: loyaltyMember 
                    } 
                  })}
                  className="w-full h-12 text-base font-medium rounded-xl"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile Cart Button - Enhanced Floating Badge */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="lg:hidden fixed bottom-4 inset-x-0 z-50 px-4"
          >
            <div className="max-w-md mx-auto">
              {/* Main cart button */}
              <Button
                onClick={() => setShowMobileCart(true)}
                className="w-full h-14 rounded-2xl shadow-2xl text-base font-bold bg-primary hover:bg-primary/90"
                data-testid="mobile-cart-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-3" />
                <span>View Cart</span>
                <span className="mx-2 px-2 py-0.5 bg-white/20 rounded-full text-sm">
                  {cartCount}
                </span>
                <span className="ml-auto font-bold text-base">${cartTotal.toFixed(2)}</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Cart Modal */}
      <AnimatePresence>
        {showMobileCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowMobileCart(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="flex justify-center py-3">
                <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-4 pb-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  <span className="font-heading text-lg text-foreground">Your Order</span>
                </div>
                <button onClick={() => setShowMobileCart(false)}>
                  <X className="w-6 h-6 text-muted-foreground" />
                </button>
              </div>

              {/* Items */}
              <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
                {cart.map((item, index) => (
                  <div key={item.cart_key || index} className="bg-background rounded-xl p-3 border border-border">
                    <div className="flex gap-3">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{item.name}</h4>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.cart_key, item.quantity - 1)}
                              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="font-medium w-8 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cart_key, item.quantity + 1)}
                              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="font-bold">${((item.price || 0) * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                      <button onClick={() => removeFromCart(item.cart_key)} className="text-destructive self-start">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-border bg-muted/30">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-2xl font-bold">${cartTotal.toFixed(2)}</span>
                </div>
                <Button 
                  onClick={() => { setShowMobileCart(false); navigate('/order'); }}
                  className="w-full h-14 text-base font-medium rounded-xl"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upsell Popup */}
      <WebUpsellPopup
        show={showUpsellPopup}
        onClose={() => setShowUpsellPopup(false)}
        cartItems={cart}
        onAddItem={handleAddUpsellItem}
        triggerItem={lastAddedItem}
      />

      {/* Accessibility Toggle */}
      <WebAccessibilityToggle
        accessibilityEnabled={accessibilityEnabled}
        setAccessibilityEnabled={setAccessibilityEnabled}
      />
    </div>
  );
}
